/**
 * One module's part of the Dashboard: a heading over a rule, folded open or
 * shut by the heading itself (unless `collapsible` is false), then the
 * module's cards. Each module gets one,
 * so the page grows a section per module. Whether a section is folded is a
 * per-browser convenience kept in localStorage; a browser that refuses
 * storage simply opens every section each visit.
 */

import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

const STORAGE_PREFIX = 'wynxx.dashboard.collapsed.';

function useCollapsed(id: string): [boolean, () => void] {
  const key = STORAGE_PREFIX + id;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    try {
      if (next) localStorage.setItem(key, '1');
      else localStorage.removeItem(key);
    } catch {
      // Not remembered; the choice still holds for this visit.
    }
  }
  return [collapsed, toggle];
}

export function DashboardModule({
  id,
  title,
  aside,
  collapsible = true,
  children,
}: {
  id: string;
  title: string;
  /** Shown after the rule, kept visible while the section is folded (a status pill). */
  aside?: ReactNode;
  /** False for a plain heading that never folds. */
  collapsible?: boolean;
  children: ReactNode;
}) {
  const [stored, toggle] = useCollapsed(id);
  const collapsed = collapsible && stored;
  const bodyId = `dash-module-${id}`;

  return (
    <section className={`dash-module${collapsed ? ' is-collapsed' : ''}`} aria-labelledby={`${bodyId}-title`}>
      <div className="dash-module-head">
        <h2 id={`${bodyId}-title`} className="dash-module-title">
          {collapsible ? (
            <button type="button" className="dash-module-toggle" aria-expanded={!collapsed} aria-controls={bodyId} onClick={toggle}>
              <ChevronDown aria-hidden="true" />
              {title}
            </button>
          ) : (
            title
          )}
        </h2>
        <span className="dash-module-rule" aria-hidden="true" />
        {aside}
      </div>
      <div id={bodyId} hidden={collapsed}>
        {children}
      </div>
    </section>
  );
}
